import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable, OnDestroy } from "@angular/core";

import { CookieService } from "ngx-cookie-service";
import {
    catchError,
    filter,
    map,
    Observable,
    of,
    Subscription,
    switchMap,
    take,
} from "rxjs";

import { v4 } from "uuid";

import { LOCALE } from "@hermes/core-app-core";

import { ShellFacade } from "@hermes/domains-shell-states-shell-state";
import {
    ENABLE_NEW_GEOFILTERING_TRAY,
    FeatureFlagFacade,
} from "@hermes/hermes-web-states-flipper-state";
import { TrayFacade } from "@hermes/hermes-web-states-tray-state";
import { Locale, LocaleUrlPrefix } from "@hermes/locale";
import { SKIP_404_INTERCEPTOR } from "@hermes/utils-generic-constants";

const COOKIE_GEO_FILTERING_TRAY = "GeoFilteringTray";

@Injectable()
export class GeofilteringTrayService implements OnDestroy {
    private locale = inject(LOCALE);
    private http = inject(HttpClient);
    private subscription = new Subscription();
    private featureFlagFacade = inject(FeatureFlagFacade);
    private readonly trayFacade = inject(TrayFacade);
    private cookieService = inject(CookieService);
    private shellFacade = inject(ShellFacade);

    public findUserLocale(
        userLocationIsoCode: string | null | undefined,
    ): Observable<Locale | undefined> {
        return this.shellFacade
            .getActivatedLocales()
            .pipe(
                map((locales) =>
                    locales.find(
                        (locale) =>
                            locale.isoCountryCode.toUpperCase() ===
                            userLocationIsoCode?.toUpperCase(),
                    ),
                ),
            );
    }

    public setGeoFilteringFilteringTrayCookieForLocale(
        urlPrefix: LocaleUrlPrefix,
    ) {
        this.cookieService.set(
            this.getGeoFilteringCookieNameForUrlPrefix(urlPrefix),
            "1",
            30,
            "/",
        );
    }

    public removeGeoFilteringFilteringTrayCookieOnCurrentLocale() {
        this.cookieService.delete(
            this.getGeoFilteringCookieNameForUrlPrefix(this.locale.urlPrefix),
            "/",
        );
    }

    public openGeofilteringTrayIfNeedeed(): void {
        this.subscription.add(
            this.featureFlagFacade
                .isActivated(ENABLE_NEW_GEOFILTERING_TRAY)
                .pipe(
                    filter(Boolean),
                    filter(() => !this.hasGeoFilteringCookieOnCurrentLocale()),
                    switchMap(() => this.fetchGeoLocation()),
                    take(1),
                )
                .subscribe((userLocationIsoCode) => {
                    this.checkGeofilteringTrayVisibility(userLocationIsoCode);
                }),
        );
    }

    /**
     * Fetch geo location
     */
    public fetchGeoLocation(): Observable<string | undefined | null> {
        // If there is a 404 call to "/geo/" service we don"t want to redirect to a 404 page
        // The added context tells the interceptor not to redirect to 404
        const context = new HttpContext().set(SKIP_404_INTERCEPTOR, true);
        return this.http
            .head<string>(`${this.locale.urlPrefix}/geo/`, {
                observe: "response",
                context,
            })
            .pipe(
                catchError(() => of(undefined)),
                map((resp) =>
                    resp && resp.headers.get("x-geo-country")
                        ? resp.headers.get("x-geo-country")
                        : undefined,
                ),
            );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private checkGeofilteringTrayVisibility(
        userLocationIsoCode: string | null | undefined,
    ): void {
        if (
            userLocationIsoCode &&
            this.locale.isoCountryCode.toUpperCase() !==
                userLocationIsoCode.toUpperCase()
        ) {
            this.openGeofilteringTray(userLocationIsoCode);
        }
    }

    private hasGeoFilteringCookieOnCurrentLocale(): boolean {
        return !!this.cookieService.get(
            this.getGeoFilteringCookieNameForUrlPrefix(this.locale.urlPrefix),
        );
    }

    private openGeofilteringTray(
        userLocationIsoCode: string | null | undefined,
    ) {
        this.trayFacade.openTray({
            uuid: v4(),
            name: "geofiltering-tray",
            titleKey: "hermes.header_notification.geo_filtering_banner.title",
            isOpen: true,
            hasOverlay: true,
            hasCategoryInstitutional: false,
            metadata: {
                userLocationIsoCode,
            },
            closeCallback: () => {
                this.setGeoFilteringFilteringTrayCookieForLocale(
                    this.locale.urlPrefix,
                );
            },
        });
    }

    private getGeoFilteringCookieNameForUrlPrefix(
        urlPrefix: LocaleUrlPrefix,
    ): string {
        return `${COOKIE_GEO_FILTERING_TRAY}_${urlPrefix}`;
    }
}
